import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiBarChart2,
    FiBookOpen,
    FiCheckCircle,
    FiAlertTriangle,
    FiSearch,
    FiPrinter,
    FiUser,
    FiClock,
    FiLayers,
    FiArrowLeft,
    FiRefreshCw,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import attendanceService from "../../services/attendanceService";
import courseService from "../../services/courseService";
import subjectService from "../../services/subjectService";
import { getYearOptions } from "../../utils/courseYears";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";

const REPORT_TYPES = [
    { value: "monthly", label: "Monthly" },
    { value: "weekly", label: "Weekly" },
    { value: "yearly", label: "Yearly" },
    { value: "custom", label: "Custom Range" },
];

const MONTHS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

const STATUS_COLORS = {
    Present: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    Late: { bg: "#fef9c3", color: "#92400e", border: "#fde047" },
    "Half Day": { bg: "#fef9c3", color: "#92400e", border: "#fde047" },
    Absent: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
    "On Leave": { bg: "#e8f1f8", color: "#002747", border: "#93c5fd" },
    "Medical Leave": { bg: "#e8f1f8", color: "#002747", border: "#93c5fd" },
    Unmarked: { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" },
};

const getToday = () => new Date().toISOString().slice(0, 10);
const getMonthStart = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
        .toISOString()
        .slice(0, 10);
};

const formatDisplayDate = (value = "") => {
    if (!value) return "—";
    const parts = value.split("-");
    if (parts.length !== 3) return value;
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
};

export default function SubjectAttendanceReport() {
    const { academicYear } = useApp();
    const [searchParams] = useSearchParams();
    const paramCourse = searchParams.get("course") || "";
    const paramYear = searchParams.get("yearNumber") || "";
    const paramStudentId = searchParams.get("studentId") || "";

    const [courses, setCourses] = useState([]);
    const [course, setCourse] = useState(paramCourse);
    const [yearNumber, setYearNumber] = useState(paramYear);
    const [subjects, setSubjects] = useState([]);
    const [subjectId, setSubjectId] = useState("");

    const [reportType, setReportType] = useState("monthly");
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [fromDate, setFromDate] = useState(getMonthStart);
    const [toDate, setToDate] = useState(getToday);

    const [search, setSearch] = useState("");
    const [selectedStudentId, setSelectedStudentId] = useState(paramStudentId);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const currentYearNum = new Date().getFullYear();
    const yearOptionsList = [currentYearNum, currentYearNum - 1, currentYearNum - 2];

    // Load active courses on mount
    useEffect(() => {
        courseService
            .getAll({ isActive: true })
            .then((res) => {
                const list = res.data || [];
                setCourses(list);
                if (list.length > 0 && !course) {
                    if (paramCourse && list.some(c => c._id === paramCourse)) {
                        setCourse(paramCourse);
                    } else {
                        setCourse(list[0]._id);
                    }
                }
            })
            .catch((err) => toast.error("Couldn't load courses: " + err.message));
    }, [course, paramCourse]);

    const selectedCourse = useMemo(
        () => courses.find((c) => c._id === course),
        [courses, course],
    );

    const yearOptions = useMemo(() => {
        return selectedCourse ? getYearOptions(selectedCourse) : [];
    }, [selectedCourse]);

    // Set default yearNumber when course changes
    useEffect(() => {
        if (paramYear && yearOptions.some((y) => String(y.yearNumber) === String(paramYear))) {
            setYearNumber(String(paramYear));
        } else if (yearOptions.length > 0 && (!yearNumber || !yearOptions.some((y) => String(y.yearNumber) === String(yearNumber)))) {
            setYearNumber(String(yearOptions[0].yearNumber));
        }
    }, [yearOptions, yearNumber, paramYear]);

    // Fetch subjects for chosen course and year
    useEffect(() => {
        if (!course || !yearNumber) {
            setSubjects([]);
            return;
        }
        subjectService
            .getAll({ course, yearNumber, isActive: true })
            .then((res) => {
                const list = Array.isArray(res) ? res : res.data || [];
                setSubjects(list);
            })
            .catch(() => setSubjects([]));
    }, [course, yearNumber]);

    // Fetch report data
    const fetchReport = useCallback(() => {
        if (!academicYear?._id) return;

        setLoading(true);
        attendanceService
            .getSubjectReport({
                academicYear: academicYear._id,
                course,
                yearNumber,
                subjectId: subjectId || undefined,
                studentId: selectedStudentId || undefined,
                reportType,
                month,
                year,
                search,
                ...(reportType === "custom" || reportType === "weekly" ? { fromDate, toDate } : {}),
            })
            .then((res) => setReportData(res.data))
            .catch((err) => toast.error(err.message || "Failed to load subject attendance report."))
            .finally(() => setLoading(false));
    }, [academicYear, course, yearNumber, subjectId, selectedStudentId, reportType, month, year, fromDate, toDate, search]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handlePrint = () => {
        window.print();
    };

    const individualReport = reportData?.individualStudentReport;
    const studentSummaries = reportData?.studentSummaries || [];
    const summary = reportData?.summary || {};
    const reportSubjects = reportData?.subjects || [];
    const range = reportData?.range || {};

    const activeCourseName = selectedCourse?.shortName || selectedCourse?.courseName || "Course";
    const activeYearLabel = yearOptions.find((y) => String(y.yearNumber) === String(yearNumber))?.label || `Year ${yearNumber}`;

    return (
        <div style={{ maxWidth: "100%", margin: "0 auto" }}>
            {/* ── Page Header ── */}
            <div className="page-header no-print" style={{ flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
                <div>
                    <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FiBarChart2 style={{ color: "var(--primary)" }} /> Subject-wise Attendance Report
                    </h1>
                    <p className="page-subtitle">
                        Lecture attendance analytics by course, year, subject, and individual student · {academicYear?.label}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    {selectedStudentId && (
                        <button
                            className="btn btn-outline"
                            onClick={() => setSelectedStudentId("")}
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                        >
                            <FiArrowLeft size={14} /> Back to Class Roster
                        </button>
                    )}
                    <button
                        className="btn btn-outline"
                        onClick={fetchReport}
                        disabled={loading}
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiRefreshCw size={13} className={loading ? "spin" : ""} /> Refresh
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handlePrint}
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPrinter size={14} /> Print Report
                    </button>
                </div>
            </div>

            {/* ── Filter Bar ── */}
            <div className="card no-print" style={{ marginBottom: "1.25rem", padding: "1rem 1.25rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "0.75rem", alignItems: "flex-end" }}>
                    {/* Course */}
                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Course</label>
                        <select
                            className="form-control"
                            style={{ fontSize: 13 }}
                            value={course}
                            onChange={(e) => {
                                setCourse(e.target.value);
                                setSubjectId("");
                                setSelectedStudentId("");
                            }}
                        >
                            {courses.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.shortName || c.courseName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Year Number */}
                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Year</label>
                        <select
                            className="form-control"
                            style={{ fontSize: 13 }}
                            value={yearNumber}
                            onChange={(e) => {
                                setYearNumber(e.target.value);
                                setSubjectId("");
                                setSelectedStudentId("");
                            }}
                        >
                            {yearOptions.map((y) => (
                                <option key={y.yearNumber} value={y.yearNumber}>
                                    {y.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Subject Filter */}
                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Subject</label>
                        <select
                            className="form-control"
                            style={{ fontSize: 13 }}
                            value={subjectId}
                            onChange={(e) => setSubjectId(e.target.value)}
                        >
                            <option value="">All Subjects</option>
                            {subjects.map((sub) => (
                                <option key={sub._id} value={sub._id}>
                                    {sub.name} {sub.code ? `(${sub.code})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Report Type */}
                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Timeframe</label>
                        <select
                            className="form-control"
                            style={{ fontSize: 13 }}
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                        >
                            {REPORT_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Month Picker when Monthly */}
                    {reportType === "monthly" && (
                        <div>
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Month</label>
                            <div style={{ display: "flex", gap: "0.35rem" }}>
                                <select
                                    className="form-control"
                                    style={{ fontSize: 13 }}
                                    value={month}
                                    onChange={(e) => setMonth(Number(e.target.value))}
                                >
                                    {MONTHS.map((m) => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="form-control"
                                    style={{ width: 85, fontSize: 13 }}
                                    value={year}
                                    onChange={(e) => setYear(Number(e.target.value))}
                                >
                                    {yearOptionsList.map((y) => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Date Range when Custom or Weekly */}
                    {(reportType === "custom" || reportType === "weekly") && (
                        <>
                            <div>
                                <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>From Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    style={{ fontSize: 13 }}
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>To Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    style={{ fontSize: 13 }}
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                        </>
                    )}

                    {/* Search student */}
                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Search Student</label>
                        <div style={{ position: "relative" }}>
                            <input
                                type="text"
                                className="form-control"
                                style={{ paddingLeft: "2rem", fontSize: 13 }}
                                placeholder="Name / Roll / Enrollment"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <FiSearch
                                size={14}
                                style={{
                                    position: "absolute",
                                    left: 9,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "var(--text-muted)",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Print Header ── */}
            <div className="print-only" style={{ marginBottom: "1.5rem" }}>
                <CollegePrintHeader
                    layout="banner"
                    logoSize={58}
                    title={`Subject Attendance Report — ${activeCourseName} (${activeYearLabel})`}
                    subtitle={`Period: ${formatDisplayDate(range.fromDate)} to ${formatDisplayDate(range.toDate)} · Academic Year: ${academicYear?.label || "—"}`}
                />
            </div>

            {/* ── Content Area ── */}
            {loading ? (
                <div className="loading-center">
                    <div className="spinner" /> Loading subject attendance report…
                </div>
            ) : individualReport ? (
                /* ══════════════════════════════════════════════════════════════════
                   MODE A: INDIVIDUAL STUDENT REPORT
                   ══════════════════════════════════════════════════════════════════ */
                <div>
                    {/* Student Info Card */}
                    <div className="card" style={{ marginBottom: "1rem", padding: "1.25rem 1.5rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                            <div style={{ display: "flex", gap: "1.25rem", alignItems: "center" }}>
                                <div
                                    style={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: "50%",
                                        background: "var(--primary-light, #e8f1f8)",
                                        color: "var(--primary, #003b67)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 22,
                                        fontWeight: 800,
                                        border: "2px solid #b6d5ec",
                                    }}
                                >
                                    {individualReport.student.fullName?.charAt(0) || "S"}
                                </div>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                                            {individualReport.student.fullName}
                                        </h2>
                                        <span className="badge badge-blue">
                                            Roll #{individualReport.student.rollNo || "—"}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4, display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                                        <span>Enrollment: <strong>{individualReport.student.enrollmentNo || "—"}</strong></span>
                                        <span>·</span>
                                        <span>Class: <strong>{activeCourseName} ({activeYearLabel})</strong></span>
                                        <span>·</span>
                                        <span>Period: <strong>{formatDisplayDate(range.fromDate)} to {formatDisplayDate(range.toDate)}</strong></span>
                                    </div>
                                </div>
                            </div>

                            {/* Overall Attendance Stat */}
                            <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.04em" }}>
                                        Overall Subject Attendance
                                    </div>
                                    <div style={{ fontSize: 24, fontWeight: 900, color: individualReport.overall.percentage >= 75 ? "#16a34a" : "#dc2626" }}>
                                        {individualReport.overall.percentage}%
                                    </div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                        {individualReport.overall.attended} attended / {individualReport.overall.totalLectures} lectures
                                    </div>
                                </div>

                                <div
                                    style={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: 14,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: individualReport.overall.percentage >= 75 ? "#dcfce7" : "#fee2e2",
                                        color: individualReport.overall.percentage >= 75 ? "#15803d" : "#dc2626",
                                    }}
                                >
                                    {individualReport.overall.percentage >= 75 ? <FiCheckCircle size={28} /> : <FiAlertTriangle size={28} />}
                                </div>
                            </div>
                        </div>

                        {/* Short Attendance Banner */}
                        {individualReport.overall.isLowAttendance && (
                            <div
                                style={{
                                    marginTop: "1rem",
                                    padding: "0.65rem 1rem",
                                    background: "#fef2f2",
                                    border: "1px solid #fecaca",
                                    borderRadius: 8,
                                    color: "#991b1b",
                                    fontSize: 13,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                }}
                            >
                                <FiAlertTriangle size={16} />
                                <span>
                                    <strong>Short Attendance Alert:</strong> This student has <strong>{individualReport.overall.percentage}%</strong> overall attendance, which is below the mandatory <strong>75%</strong> requirement.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Subject-Wise Summary Table */}
                    <div className="card" style={{ marginBottom: "1.25rem" }}>
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontWeight: 700 }}>
                                <FiLayers size={14} /> Subject Breakdown
                            </span>
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                {individualReport.bySubject.length} Subjects
                            </span>
                        </div>
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Subject</th>
                                        <th>Code</th>
                                        <th>Type</th>
                                        <th style={{ textAlign: "center" }}>Held</th>
                                        <th style={{ textAlign: "center" }}>Attended</th>
                                        <th style={{ textAlign: "center" }}>Absent</th>
                                        <th style={{ textAlign: "center" }}>Late</th>
                                        <th style={{ textAlign: "center" }}>Leave</th>
                                        <th style={{ minWidth: 150 }}>Attendance Rate</th>
                                        <th style={{ textAlign: "center" }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {individualReport.bySubject.map((s, idx) => {
                                        const pctColor = s.percentage >= 75 ? "#16a34a" : s.percentage >= 60 ? "#d97706" : "#dc2626";
                                        return (
                                            <tr key={s.subjectId || idx}>
                                                <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{idx + 1}</td>
                                                <td style={{ fontWeight: 600, fontSize: 13 }}>{s.subjectName}</td>
                                                <td>
                                                    {s.code ? <span className="badge badge-blue">{s.code}</span> : "—"}
                                                </td>
                                                <td style={{ fontSize: 12 }}>{s.type}</td>
                                                <td style={{ textAlign: "center", fontWeight: 600 }}>{s.totalLectures}</td>
                                                <td style={{ textAlign: "center", color: "#16a34a", fontWeight: 700 }}>{s.attended}</td>
                                                <td style={{ textAlign: "center", color: "#dc2626" }}>{s.absent}</td>
                                                <td style={{ textAlign: "center", color: "#d97706" }}>{s.late}</td>
                                                <td style={{ textAlign: "center", color: "#003b67" }}>{s.leave}</td>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                        <div style={{ flex: 1, background: "#e2e8f0", height: 7, borderRadius: 99, overflow: "hidden" }}>
                                                            <div style={{ width: `${Math.min(s.percentage, 100)}%`, background: pctColor, height: "100%" }} />
                                                        </div>
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: pctColor, minWidth: 42, textAlign: "right" }}>
                                                            {s.percentage}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    {s.totalLectures === 0 ? (
                                                        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>No Classes</span>
                                                    ) : s.isLowAttendance ? (
                                                        <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                                                            Short (&lt;75%)
                                                        </span>
                                                    ) : (
                                                        <span style={{ background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                                                            Satisfactory
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Detailed Lecture Log */}
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontWeight: 700 }}>
                                <FiClock size={14} /> Detailed Lecture History
                            </span>
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                {individualReport.lectureLog.length} Recorded Sessions
                            </span>
                        </div>
                        {individualReport.lectureLog.length === 0 ? (
                            <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                                No lecture attendance recorded for this period.
                            </div>
                        ) : (
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Time / Period</th>
                                            <th>Subject</th>
                                            <th>Type</th>
                                            <th style={{ textAlign: "center" }}>Status</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {individualReport.lectureLog.map((log, idx) => {
                                            const sc = STATUS_COLORS[log.status] || STATUS_COLORS.Unmarked;
                                            return (
                                                <tr key={idx}>
                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{idx + 1}</td>
                                                    <td style={{ fontWeight: 600, fontSize: 13 }}>{formatDisplayDate(log.attendanceDate)}</td>
                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                        Period #{log.periodNo} {log.startTime && log.endTime ? `(${log.startTime}–${log.endTime})` : ""}
                                                    </td>
                                                    <td style={{ fontWeight: 600, fontSize: 13 }}>
                                                        {log.subjectName} {log.code && <span className="badge badge-blue" style={{ marginLeft: 4 }}>{log.code}</span>}
                                                    </td>
                                                    <td style={{ fontSize: 12 }}>{log.type}</td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <span
                                                            style={{
                                                                background: sc.bg,
                                                                color: sc.color,
                                                                border: `1px solid ${sc.border}`,
                                                                padding: "2px 9px",
                                                                borderRadius: 99,
                                                                fontSize: 11.5,
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                        {log.remarks || "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* ══════════════════════════════════════════════════════════════════
                   MODE B: CLASS / SUBJECT SUMMARY ROSTER
                   ══════════════════════════════════════════════════════════════════ */
                <div>
                    {/* KPI Cards */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                        <div className="card" style={{ padding: "1.1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Total Lectures Held</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)", marginTop: 4 }}>
                                {summary.lecturesHeld || 0}
                            </div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>In selected period</div>
                        </div>

                        <div className="card" style={{ padding: "1.1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Enrolled Students</div>
                            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>
                                {summary.totalStudents || 0}
                            </div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>{activeCourseName} ({activeYearLabel})</div>
                        </div>

                        <div className="card" style={{ padding: "1.1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Average Attendance Rate</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: summary.presentPercentage >= 75 ? "#16a34a" : "#dc2626", marginTop: 4 }}>
                                {summary.presentPercentage || 0}%
                            </div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>Across all marked lectures</div>
                        </div>

                        <div className="card" style={{ padding: "1.1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Students &lt; 75% Attendance</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: summary.lowAttendanceCount > 0 ? "#dc2626" : "#16a34a", marginTop: 4 }}>
                                {summary.lowAttendanceCount || 0}
                            </div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>Require attendance warning</div>
                        </div>
                    </div>

                    {/* Class Roster Table */}
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontWeight: 700 }}>
                                <FiUser size={14} /> Student Attendance Roster ({studentSummaries.length})
                            </span>
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                Click "View Breakdown" to see individual subject records
                            </span>
                        </div>

                        {studentSummaries.length === 0 ? (
                            <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2.5rem" }}>
                                <FiBookOpen size={36} style={{ opacity: 0.25, display: "block", margin: "0 auto 0.75rem" }} />
                                <p style={{ fontSize: 14 }}>No attendance records found for this class and date range.</p>
                            </div>
                        ) : (
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Roll No</th>
                                            <th>Student Name</th>
                                            <th>Enrollment No</th>
                                            <th style={{ textAlign: "center" }}>Lectures</th>
                                            <th style={{ textAlign: "center" }}>Attended</th>
                                            <th style={{ textAlign: "center" }}>Absent</th>
                                            {/* Top 3-4 subjects as sample columns if multiple */}
                                            {reportSubjects.slice(0, 3).map((sub) => (
                                                <th key={sub._id} style={{ textAlign: "center", fontSize: 11.5 }}>
                                                    {sub.name.slice(0, 14)}…
                                                </th>
                                            ))}
                                            <th style={{ minWidth: 130 }}>Overall %</th>
                                            <th style={{ textAlign: "right" }} className="no-print">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {studentSummaries.map((entry) => {
                                            const s = entry.student;
                                            const pct = entry.attendancePercentage;
                                            const pctColor = pct >= 75 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";
                                            return (
                                                <tr
                                                    key={s._id}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setSelectedStudentId(s._id)}
                                                    className="hover-row"
                                                >
                                                    <td style={{ fontWeight: 600 }}>{s.rollNo || "—"}</td>
                                                    <td>
                                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.fullName}</div>
                                                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.mobile || ""}</div>
                                                    </td>
                                                    <td><span className="badge badge-blue">{s.enrollmentNo || "—"}</span></td>
                                                    <td style={{ textAlign: "center", fontWeight: 600 }}>{entry.totalLectures}</td>
                                                    <td style={{ textAlign: "center", color: "#16a34a", fontWeight: 700 }}>{entry.presentCount}</td>
                                                    <td style={{ textAlign: "center", color: "#dc2626" }}>{entry.absentCount}</td>

                                                    {/* Subject breakdown cells */}
                                                    {reportSubjects.slice(0, 3).map((sub) => {
                                                        const match = (entry.subjectBreakdown || []).find((b) => String(b.subjectId) === String(sub._id));
                                                        return (
                                                            <td key={sub._id} style={{ textAlign: "center", fontSize: 12 }}>
                                                                {match && match.total > 0 ? (
                                                                    <span style={{ fontWeight: 600, color: match.percentage >= 75 ? "#16a34a" : "#dc2626" }}>
                                                                        {match.percentage}%
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: "var(--text-muted)" }}>—</span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}

                                                    <td>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                            <div style={{ flex: 1, background: "#e2e8f0", height: 6, borderRadius: 99, overflow: "hidden" }}>
                                                                <div style={{ width: `${Math.min(pct, 100)}%`, background: pctColor, height: "100%" }} />
                                                            </div>
                                                            <span style={{ fontSize: 12, fontWeight: 700, color: pctColor, minWidth: 38, textAlign: "right" }}>
                                                                {pct}%
                                                            </span>
                                                        </div>
                                                    </td>

                                                    <td style={{ textAlign: "right" }} className="no-print">
                                                        <button
                                                            className="btn btn-outline btn-sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedStudentId(s._id);
                                                            }}
                                                            style={{ fontSize: 11.5, padding: "3px 8px" }}
                                                        >
                                                            View Breakdown →
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
